/**
 * Sync transport: drains the outbox to the server and pulls changes back.
 *
 * The outbox owns the protocol (see `outbox.ts`); this module owns the network
 * and the retry schedule.
 *
 * Retry policy — deliberately dumb and deliberately not Background Sync.
 * WebKit still does not ship the Background Sync API, and even where it exists
 * the service worker may be suspended the moment the app is closed. So the app
 * retries on the six moments it can actually observe: launch,
 * `visibilitychange` to visible, the `online` event, an explicit user tap, a
 * failure of its own, and a local write. Anything queued while the app is shut
 * simply waits until the app is opened again, which for a post-game review app
 * around a table is the correct trade.
 *
 * That fifth moment was missing and it cost an evening. The first four are all
 * events the browser hands us, and a push that comes back 500 produces none of
 * them: the device never went offline, the tab never hid, and the rating screen
 * carries no sync affordance to tap. So the queue stopped where it was, the
 * server never got the rating, and the reveal — which is the server's decision —
 * never came. What the table saw was a phone insisting it was waiting on
 * everybody else. Only a reload flushed it. A failure now schedules its own next
 * attempt, and every trigger goes through `recoverNow` so there is exactly one
 * retry schedule in the app rather than one per write path.
 *
 * The sixth was missing too, everywhere except the rating path, and it wedged
 * one layer up in the same shape. `mutate` makes a change durable and
 * notifies; the only thing listening here was the unsynced badge, so a saved
 * player or a reordered aspect sat in the queue reading "1 unsynced" until an
 * `online` event happened past or somebody found "Sync now" in settings. Ratings
 * had bought their way out by calling `syncNow` at the commit, and copying that
 * line into the other thirteen write paths would have been thirteen places to
 * forget it next time. The queue announces its own work instead.
 */

import type { Entity, MutationResult, PullResponse, PushResponse } from "../../shared/types";
import { SYNCABLE } from "../../shared/types";
import {
  ALL_STORES,
  CONFLICT_STORE,
  DEAD_STORE,
  OUTBOX_STORE,
  bulkPut,
  getAll,
  getMeta,
  idbAvailable,
  setMeta,
  transaction,
  type StoreName,
} from "./idb";
import {
  applyResults,
  conflicts,
  deadLetters,
  discardDeadLetter as dropDeadLetter,
  getOutboxStorage,
  outboxPaused,
  pauseOutbox,
  pending,
  pendingCount,
  readLocal,
  removeCurrent,
  resumeOutbox,
  subscribeOutbox,
  type Row,
} from "./outbox";

const CURSOR_KEY = "sync_cursor";
const BACKUP_KEY = "last_backup_at";
const ACCOUNT_KEY = "auth_user_id";
const PUSH_BATCH = 100;

export type SyncStatus = "idle" | "syncing" | "offline" | "paused" | "error";

export interface SyncState {
  status: SyncStatus;
  pending: number;
  conflicts: number;
  deadLetters: number;
  cursor: number;
  lastSyncAt: number | null;
  lastError: string | null;
}

let state: SyncState = {
  status: "idle",
  pending: 0,
  conflicts: 0,
  deadLetters: 0,
  cursor: 0,
  lastSyncAt: null,
  lastError: null,
};

const listeners = new Set<(s: SyncState) => void>();

/** Subscribe to sync state. Returns an unsubscribe function. */
export function subscribeSync(fn: (s: SyncState) => void): () => void {
  listeners.add(fn);
  fn(state);
  return () => listeners.delete(fn);
}

export function getSyncState(): SyncState {
  return state;
}

function setState(patch: Partial<SyncState>): void {
  state = { ...state, ...patch };
  for (const fn of listeners) fn(state);
}

async function refreshCounts(): Promise<void> {
  if (!idbAvailable()) return;
  const [p, c, d] = await Promise.all([pendingCount(), conflicts(), deadLetters()]);
  setState({ pending: p, conflicts: c.length, deadLetters: d.length });
}

// ---------------------------------------------------------------------------
// Cursor
// ---------------------------------------------------------------------------

export async function getCursor(): Promise<number> {
  return (await getMeta<number>(CURSOR_KEY)) ?? 0;
}

async function setCursor(value: number): Promise<void> {
  await setMeta(CURSOR_KEY, value);
  setState({ cursor: value });
}

// ---------------------------------------------------------------------------
// Push / pull
// ---------------------------------------------------------------------------

class AuthError extends Error {}

const authLostListeners = new Set<() => void>();

/**
 * Fired when the server has stopped recognising this device.
 *
 * Pausing the outbox is the right thing to do with the *queue*, but on its own
 * it is invisible: the only place that state surfaces is `/settings/sync`, so an
 * expired cookie used to read as "nothing is reaching the table" for the rest of
 * the evening. The auth layer subscribes to this and puts the sign-in page back
 * in front of the user. Nothing here touches the queue — the pause is what keeps
 * the unsynced work, and it is lifted by `retrySync` once there is a session
 * again.
 */
export function subscribeAuthLost(fn: () => void): () => void {
  authLostListeners.add(fn);
  return () => {
    authLostListeners.delete(fn);
  };
}

function loseAuth(): void {
  // Pause, never clear. The queue outlives the session.
  pauseOutbox("Signed out — your unsynced changes are kept.");
  setState({ status: "paused" });
  for (const fn of authLostListeners) fn();
}

/**
 * Bind this device's local store to an account, wiping it if it belonged to a
 * different one.
 *
 * Signup is open, so two accounts can meet on one browser — a housemate signing
 * in on the kitchen tablet. IndexedDB is per-origin, not per-account, so without
 * this the second account inherits the first one's library on screen, and worse,
 * inherits its sync cursor and therefore pulls nothing that happened before it.
 * Neither is recoverable by re-syncing, so the switch is handled at the door.
 *
 * A wipe is only reachable by signing in as someone else, which is an explicit
 * act; signing the same account back in never touches the store, and signing out
 * never touches it at all.
 */
export async function adoptAccount(userId: string): Promise<boolean> {
  if (!idbAvailable()) return false;
  const previous = await getMeta<string>(ACCOUNT_KEY);
  const switched = previous !== undefined && previous !== userId;
  if (switched) await wipeLocal();
  // Written after the wipe: `wipeLocal` clears the meta store along with
  // everything else, cursor included.
  await setMeta(ACCOUNT_KEY, userId);
  return switched;
}

async function postJson<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(body),
  });
  if (res.status === 401 || res.status === 403) throw new AuthError("not authenticated");
  if (!res.ok) throw new Error(`${url} -> ${res.status}`);
  return (await res.json()) as T;
}

/**
 * Drain the outbox. Returns the number of ops that left the queue (settled,
 * conflicted or dead-lettered). Safe to call concurrently — the second call is
 * a no-op while the first is in flight.
 */
export async function push(): Promise<number> {
  if (!idbAvailable()) return 0;
  if (outboxPaused().paused) {
    setState({ status: "paused" });
    return 0;
  }
  let drained = 0;
  for (;;) {
    const ops = await pending(PUSH_BATCH);
    if (ops.length === 0) break;
    let response: PushResponse;
    try {
      response = await postJson<PushResponse>("/api/sync/push", {
        mutations: ops.map((op) => ({
          op_id: op.op_id,
          entity: op.entity,
          id: op.id,
          base_version: op.base_version,
          patch: op.patch,
          ...(op.deleted ? { deleted: true } : {}),
        })),
      });
    } catch (err) {
      if (err instanceof AuthError) {
        loseAuth();
      } else {
        setState({
          status: navigator.onLine === false ? "offline" : "error",
          lastError: err instanceof Error ? err.message : String(err),
        });
      }
      return drained;
    }
    const summary = await applyResults(ops, response.results as MutationResult[]);
    // A refusal changed nothing on the server, so the row this device wrote
    // optimistically is now the only copy of it anywhere. Left alone it survives
    // a reload and renders next to the revealed answer it disagrees with, with
    // nothing on screen admitting the two differ — the same silent divergence
    // `discardDeadLetter` was written to end, arriving without anybody having to
    // find the sync screen first. The dead letter stays: the op is still the
    // user's to see, retry or throw away.
    for (const op of summary.rejectedOps) {
      // A restore that cannot reach the server is not worth failing a drain
      // over; the row is stale, which the next successful pull repairs.
      await restoreFromServer(op.entity, op.id).catch(() => undefined);
    }
    drained += summary.applied + summary.duplicate + summary.conflict + summary.rejected;
    // Nothing left the queue: pushing again would spin on the same batch.
    //
    // The batch is still owed, though, and a 200 fires none of the event
    // triggers, so breaking out quietly would strand the queue with nothing
    // scheduled to come back for it — the same wedge an outright 5xx used to
    // cause. Report it and arm the ladder, exactly as the failure path does.
    if (summary.unanswered === ops.length) {
      setState({ status: "error", lastError: "server did not answer the batch" });
      armRetry();
      break;
    }
    if (ops.length < PUSH_BATCH) break;
  }
  await refreshCounts();
  return drained;
}

/** The four tables the server redacts on the wire while a rating is blind. */
const RATING_ENTITIES: ReadonlySet<string> = new Set([
  "headline_rating",
  "overall_rating",
  "aspect_rating",
  "subquestion_rating",
]);

/**
 * Reconcile one incoming rating row with the copy this device already holds.
 *
 * Blind entry is enforced on the way out of the database: while a rating is
 * unrevealed the server sends `value: null` (and `note: null`) to *every*
 * device, including the one that wrote the number. That redaction is correct on
 * the wire — on a shared device the phone is handed around — but writing it
 * straight into the local store erases the answer this device authored, so the
 * player comes back to an aspect they already rated, sees a blank, and enters a
 * different number than the one the table will reveal.
 *
 * A genuine value always wins, including a genuine "not applicable". The server
 * redacts `na` alongside the value now — shipping "doesn't apply to me" while
 * withholding the number told the owner's device what somebody had said a whole
 * reveal early — so a blind row arrives as `value: null` with `na: null` (or
 * with no `na` column at all, as on `headline_rating`), and `na: 1` can only
 * come from a row the server has already unblinded. That is the whole test:
 * `na === 1` is real information, anything else alongside a null value is the
 * redaction, and only then, and only when this device actually holds an answer,
 * is the local answer kept.
 *
 * What that costs, knowingly: while an aspect is blind this device cannot tell
 * a redaction from another of the owner's devices re-rating the same seat, so it
 * keeps its own answer until the reveal replay corrects it. The wire is
 * deliberately symmetric there — anything that told the two apart would tell the
 * phone in front of the wrong player what the answer was.
 */
export function mergeIncomingRating(
  entity: Entity,
  incoming: Record<string, unknown>,
  local: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!RATING_ENTITIES.has(entity)) return incoming;
  if (incoming["value"] !== null && incoming["value"] !== undefined) return incoming;
  if (incoming["na"] === 1) return incoming;
  if (!local) return incoming;
  const answered = (local["value"] !== null && local["value"] !== undefined) || local["na"] === 1;
  if (!answered) return incoming;
  const merged: Record<string, unknown> = { ...incoming, value: local["value"] ?? null };
  if ("na" in local) merged["na"] = local["na"];
  // The redaction nulls the note alongside the value, so it is restored on the
  // same evidence.
  if ("note" in incoming && local["note"] !== undefined) merged["note"] = local["note"];
  return merged;
}

/** Fetch everything changed since our cursor and land it locally. */
export async function pull(): Promise<number> {
  if (!idbAvailable()) return 0;
  const since = await getCursor();
  const res = await fetch(`/api/sync/pull?since=${since}`, { credentials: "same-origin" });
  if (res.status === 401 || res.status === 403) {
    loseAuth();
    return 0;
  }
  if (!res.ok) throw new Error(`/api/sync/pull -> ${res.status}`);
  const body = (await res.json()) as PullResponse;

  let applied = 0;
  for (const entity of SYNCABLE) {
    const rows = body.changes[entity];
    if (!rows || rows.length === 0) continue;
    // Tombstones are removed locally rather than kept as dead rows: the cursor
    // means we will never be asked to re-apply them, and screens should not
    // have to filter `deleted` on every read.
    const upserts = rows.filter((r) => r.deleted !== 1 && r.deleted !== true);
    const tombstones = rows.filter((r) => r.deleted === 1 || r.deleted === true);
    if (upserts.length > 0) {
      const landing = RATING_ENTITIES.has(entity)
        ? await Promise.all(
            upserts.map(async (row) =>
              mergeIncomingRating(entity, row, await readLocal(entity, String(row["id"]))),
            ),
          )
        : upserts;
      await bulkPut(entity, landing);
    }
    if (tombstones.length > 0) {
      await transaction([entity], "readwrite", async (tx) => {
        for (const row of tombstones) await tx.store(entity).delete(String(row.id));
      });
    }
    applied += rows.length;
  }
  await setCursor(body.cursor);
  return applied;
}

/**
 * Land a row the server has just handed back, without waiting for a pull.
 *
 * Some of what the server decides arrives as the answer to a request rather than
 * on the sync channel — completing a session is the one that matters, because
 * the person who tapped it is watching. The row it answers with is authoritative
 * and is written as-is: this is the same landing `pull` does, on one row, early.
 *
 * No outbox op is appended. Nothing here is a change of *ours* to send back, and
 * queueing one would push `status: COMPLETED` into the very guard that refuses
 * writes to a finished session — a dead letter for a night that went perfectly.
 *
 * Counts are refreshed afterwards for the notification rather than the numbers:
 * the screens reading the local store watch this state to know it moved, and a
 * row landed in silence is a row nothing re-reads.
 */
export async function adoptServerRow<T extends { id: string }>(
  entity: Entity,
  row: T,
): Promise<void> {
  await getOutboxStorage().commit(entity, row as unknown as Row, null);
  await refreshCounts();
}

// ---------------------------------------------------------------------------
// Recovery
// ---------------------------------------------------------------------------

/**
 * How long to wait before trying again, per consecutive failure.
 *
 * The first step is short because the overwhelmingly common case is a single
 * unlucky request — a dropped connection as the phone moves between the flat's
 * two access points, a server restart — and the table is sitting there waiting
 * on the reveal. It stops climbing at five minutes rather than growing without
 * bound: this is a queue of ratings people are physically waiting on, so the
 * worst case has to stay inside the length of an argument about whether the game
 * was too long. An hour-long backoff would be indistinguishable from the bug.
 */
const BACKOFF_MS: readonly number[] = [1_000, 5_000, 15_000, 60_000, 300_000];

let retryTimer: ReturnType<typeof setTimeout> | null = null;
let retryStep = 0;
/** Writes the HTTP path is still holding. See `reportUnsentWrites`. */
let owedOverHttp = 0;

const recoveryListeners = new Set<() => void>();

/**
 * Ride the sync transport's retry schedule.
 *
 * Not everything the app owes the server goes through the outbox: a guest phone
 * and a multi-device night write over plain HTTP, remember what the server
 * refused, and name it on screen. That list had the same shape of bug this
 * module just grew out of — it waited for somebody to notice the banner and tap
 * it. Subscribing here means it is retried on exactly the moments the queue is,
 * rather than growing a second schedule that drifts away from this one.
 *
 * Listeners run before the queue is drained, and must not throw.
 */
export function subscribeRecovery(fn: () => void): () => void {
  recoveryListeners.add(fn);
  return () => {
    recoveryListeners.delete(fn);
  };
}

/**
 * How many writes the HTTP path is still holding, as it holds them.
 *
 * The outbox drags the schedule along behind it: a push that fails arms the
 * ladder from inside `push`, so the queue is never left with nothing coming back
 * for it. The other write path — every guest phone, and the owner on a
 * multi-device night — has nothing to drag. Its ratings live in the rating
 * screen's memory, so a `POST /api/ratings` that comes back 500 leaves an empty
 * queue, fires no event, and used to arm nothing whatsoever: one POST, then
 * silence, under a banner telling the table it was retrying. It was not.
 *
 * Reported as a count rather than armed directly because the debt outlives one
 * attempt: `syncNow` finishing cleanly must not cancel the only schedule this
 * path has. And a schedule nobody is listening on cannot pay anything back, so
 * the arming is conditional on there being a `subscribeRecovery` subscriber —
 * the rating screen — still on the other end.
 */
export function reportUnsentWrites(count: number): void {
  owedOverHttp = count;
  armRecovery();
}

/** Keep the ladder running while work outside the outbox is still owed. */
function armRecovery(): void {
  if (owedOverHttp === 0 || recoveryListeners.size === 0) return;
  armRetry();
}

function cancelRetry(): void {
  if (retryTimer !== null) clearTimeout(retryTimer);
  retryTimer = null;
  retryStep = 0;
}

/**
 * Schedule the next attempt. One timer at a time: a batch of failed writes is
 * one thing to recover from, not one per write.
 */
function armRetry(): void {
  if (retryTimer !== null) return;
  const wait = BACKOFF_MS[Math.min(retryStep, BACKOFF_MS.length - 1)] ?? 0;
  retryStep += 1;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    void recoverNow();
  }, wait);
  // A pending retry is never a reason to keep a process alive; in a browser this
  // is a no-op, and under a test runner it is the difference between a suite
  // that exits and one that hangs.
  (retryTimer as unknown as { unref?: () => void }).unref?.();
}

/**
 * Every trigger goes through here, so the outbox and the HTTP write path can
 * never end up on different retry schedules.
 */
function recoverNow(): Promise<void> {
  for (const fn of recoveryListeners) {
    try {
      fn();
    } catch {
      // A subscriber's own failure is not the transport's to report, and must
      // not stop the queue from draining.
    }
  }
  return syncNow();
}

let inFlight: Promise<void> | null = null;

/** Push then pull. This is what every trigger below calls. */
export async function syncNow(): Promise<void> {
  if (!idbAvailable()) return;
  if (inFlight) return inFlight;
  inFlight = (async () => {
    setState({ status: "syncing", lastError: null });
    try {
      await push();
      // `push` reports a transport failure by moving the status rather than by
      // throwing — a batch that died halfway has still settled everything ahead
      // of it — so reading the status back is how this function finds out. It
      // has to: a pull that then succeeded used to overwrite the failure with
      // "idle", clear `lastError`, and leave the only status line in the app
      // saying everything had reached the table while the queue was still full.
      const pushFailed = state.status === "error" || state.status === "offline";
      await pull();
      // A paused queue is waiting on a sign-in, not on the network. Retrying it
      // would spin against a server that is right to refuse.
      if (outboxPaused().paused) return;
      if (pushFailed) {
        armRetry();
        return;
      }
      setState({ status: "idle", lastSyncAt: Date.now(), lastError: null });
      // A healthy transport is not the same as a table that is caught up. A
      // rating the HTTP path is still holding has no queue to keep it on the
      // schedule, so cancelling here would take away the only clock coming back
      // for it. The step is left where it is rather than reset: the ladder is
      // measured from the failure, not from the last time the network worked.
      if (owedOverHttp > 0) armRecovery();
      else cancelRetry();
    } catch (err) {
      setState({
        status: navigator.onLine === false ? "offline" : "error",
        lastError: err instanceof Error ? err.message : String(err),
      });
      // Includes a failed pull, which is how a device misses the reveal: the
      // table's gems landed on the server and this phone never heard.
      if (!outboxPaused().paused) armRetry();
    } finally {
      await refreshCounts();
      inFlight = null;
    }
  })();
  return inFlight;
}

// ---------------------------------------------------------------------------
// Triggers
// ---------------------------------------------------------------------------

let started = false;

/**
 * Wire the retry triggers. Idempotent. Call once from `main.tsx`.
 * Returns a teardown function, mostly for tests and hot reload.
 */
export function startSync(): () => void {
  if (started) return () => undefined;
  started = true;

  const onVisible = () => {
    if (document.visibilityState === "visible") void recoverNow();
  };
  const onOnline = () => {
    // The connection is back, so start the ladder over: whatever was failing a
    // minute ago deserves an immediate attempt rather than the rest of a wait
    // that was sized for a server that would not answer.
    cancelRetry();
    setState({ status: "idle" });
    void recoverNow();
  };
  const onOffline = () => setState({ status: "offline" });

  /**
   * A local write is a trigger, not just a number for the badge.
   *
   * Counted first and pushed only on new work: `applyResults`, `pauseOutbox` and
   * `resumeOutbox` notify through here as well, and syncing in response to a
   * queue that has just emptied would spin against a server with nothing to say.
   * A paused queue is waiting on a sign-in rather than on the network, and
   * pushing it would re-run the auth failure — and the sign-in page it throws up
   * — on every row the settings screen saves.
   *
   * `syncNow` rather than `recoverNow`: the HTTP write path's retry list has no
   * stake in a profile being renamed, and firing it on every local write would
   * be the second retry schedule this module exists to avoid.
   */
  const onLocalWrite = () => {
    void (async () => {
      await refreshCounts();
      if (state.pending === 0 || outboxPaused().paused) return;
      void syncNow().catch(() => undefined);
    })();
  };

  document.addEventListener("visibilitychange", onVisible);
  window.addEventListener("online", onOnline);
  window.addEventListener("offline", onOffline);
  const unsubscribeOutbox = subscribeOutbox(onLocalWrite);

  void (async () => {
    setState({ cursor: await getCursor() });
    await refreshCounts();
    await recoverNow();
  })();

  return () => {
    document.removeEventListener("visibilitychange", onVisible);
    window.removeEventListener("online", onOnline);
    window.removeEventListener("offline", onOffline);
    unsubscribeOutbox();
    cancelRetry();
    started = false;
  };
}

/** The explicit user action trigger — wire this to the "sync now" affordance. */
export function retrySync(): Promise<void> {
  resumeOutbox();
  // A tap is a fresh start, not a place in a queue: whatever the backoff had
  // grown to, the person is here now and asking.
  cancelRetry();
  return recoverNow();
}

// ---------------------------------------------------------------------------
// Taking back a create that failed
// ---------------------------------------------------------------------------

/** Whether the row is really gone from everyone's shelf, or survived up there. */
export type Rollback = "removed" | "kept";

/** Ops still owed for one row, and refusals already parked against it. */
async function outstanding(entity: Entity, id: string): Promise<{ queued: number; refused: number }> {
  const mine = (op: { entity: string; id: string }): boolean => op.entity === entity && op.id === id;
  const [queued, stuck, dead] = await Promise.all([pending(), conflicts(), deadLetters()]);
  return {
    queued: queued.filter(mine).length,
    refused: [...stuck, ...dead].filter((record) => mine(record.op)).length,
  };
}

/**
 * Take back a row this device minted a moment ago, and say whether it worked.
 *
 * Why a rollback exists at all, rather than the obvious alternative of not
 * pushing until the create succeeds: `POST /api/sessions` refuses a `game_id`
 * the server does not already hold (`src/server/sessions.ts` — "unknown game",
 * 404), so the game row *must* reach the server before the session is asked for.
 * Holding it back would trade a phantom game for a night that cannot start at
 * all. The create is optimistic by necessity; the undo is what has to be sound.
 *
 * Two things went wrong before, and they are the same thing twice. The tombstone
 * was sent at base 0 — right for a row the server has never heard of, wrong for
 * the row it had just accepted at version 1 — so the delete came back a conflict
 * and the phantom game sat on every other device under a notice saying nothing
 * had been saved. And the caller could not tell: `undoGame` returned nothing, so
 * a rollback that failed and one that worked looked identical from the screen
 * that had to describe them.
 *
 * So the base comes from the row itself (`removeCurrent`), and the answer comes
 * back. `"kept"` is not an error to swallow — it is the one case where the table
 * must be told the game is still on the shelf.
 *
 * The drain is repeated rather than awaited once: `syncNow` hands back a drain
 * that is already running instead of starting a second one, and a drain that
 * started before this tombstone was written cannot have carried it. Taking that
 * promise as the answer would report a clean shelf over a row that never moved.
 */
export async function rollbackCreate(entity: Entity, id: string): Promise<Rollback> {
  const local = await readLocal(entity, id);
  // Nothing here to take back, so nothing can survive: no local row means the
  // create never even got as far as this device's own store.
  if (!local) return "removed";
  // A row the server has never acknowledged has no copy up there to become a
  // phantom. Its create is still in the queue, and the tombstone stacks directly
  // behind it, so the pair nets out whenever the queue finally drains.
  const version = local["version"];
  const onServer = typeof version === "number" && version > 0;

  if (!(await removeCurrent(entity, id))) return "removed";

  for (let attempt = 0; attempt < 2; attempt += 1) {
    await syncNow();
    if ((await outstanding(entity, id)).queued === 0) break;
  }

  const { queued, refused } = await outstanding(entity, id);
  // A refusal is the case the whole return value exists for: the row is up
  // there, this device cannot remove it, and the conflict stays on the sync
  // screen where the user can resolve it.
  if (refused > 0) return "kept";
  if (queued === 0) return "removed";
  return onServer ? "kept" : "removed";
}

// ---------------------------------------------------------------------------
// A night the server does not have
// ---------------------------------------------------------------------------

/**
 * The entities that only exist because a night does, mirroring
 * `SESSION_CHILD_TABLES` in `src/server/sessions.ts`. `play_again` is absent for
 * the same reason it is absent there: it hangs off (profile, game), not off a
 * session.
 */
const SESSION_CHILD_ENTITIES: readonly Entity[] = [
  "headline_rating",
  "overall_rating",
  "aspect_rating",
  "subquestion_rating",
  "session_subquestion",
  "session_aspect",
  "session_participant",
];

/** Whether a queued or refused op is about this night. */
function namesSession(op: { entity: Entity; id: string; patch: Record<string, unknown> }, sessionId: string): boolean {
  if (op.entity === "play_session") return op.id === sessionId;
  return op.patch["session_id"] === sessionId;
}

/**
 * Take a night off this device when the server does not have one to tombstone.
 *
 * Normally nothing needs this: `deleteSession` writes tombstones, the pull
 * carries them down, and `pull` deletes the local rows. That whole chain assumes
 * the server still holds the row, and there is one case where it does not — a
 * `DELETE` answered `404`. Then no tombstone is ever minted, so no pull can ever
 * carry one, and the night is stuck on the device for good: every later tap gets
 * the same 404, and the card stays on the home screen through any number of
 * reloads. That is not hypothetical, it is what this repo's own dev database
 * produced the day a checkout was pointed at a database that had been reset
 * under it — the rows had server-assigned `updated_seq` values from a database
 * that no longer existed.
 *
 * So the delete is done here instead, in the same shape `pull` uses for a
 * tombstone: children first, then the session row. Deepest-first is not
 * load-bearing — these are separate transactions and there are no foreign keys
 * in IndexedDB — but it matches the server's list so the two can be read against
 * each other.
 *
 * The refusals parked against the night go too. A dead letter is a rating for a
 * session that is now gone from this device; leaving it would keep "1 refused
 * write" on the sync screen pointing at a night the person deliberately deleted,
 * which is an error report for something that went exactly as asked.
 */
export async function forgetSession(sessionId: string): Promise<void> {
  if (!idbAvailable()) return;

  for (const entity of SESSION_CHILD_ENTITIES) {
    const rows = await getAll<Record<string, unknown>>(entity);
    const mine = rows.filter((row) => row["session_id"] === sessionId);
    if (mine.length === 0) continue;
    await transaction([entity], "readwrite", async (tx) => {
      for (const row of mine) await tx.store(entity).delete(String(row["id"]));
    });
  }

  await transaction(["play_session"], "readwrite", async (tx) => {
    await tx.store("play_session").delete(sessionId);
  });

  for (const letter of await deadLetters()) {
    if (namesSession(letter.op, sessionId)) await dropDeadLetter(letter.op_id);
  }

  await refreshCounts();
}

// ---------------------------------------------------------------------------
// Discarding a refused write
// ---------------------------------------------------------------------------

/** The server's copy of one row, asked for through the ordinary pull route. */
async function fetchServerRow(
  entity: Entity,
  id: string,
  since: number,
): Promise<Record<string, unknown> | undefined> {
  const res = await fetch(`/api/sync/pull?since=${since}`, { credentials: "same-origin" });
  if (res.status === 401 || res.status === 403) {
    loseAuth();
    throw new Error("Signed out, so the server could not be asked what this row says.");
  }
  if (!res.ok) throw new Error(`/api/sync/pull -> ${res.status}`);
  const body = (await res.json()) as PullResponse;
  return (body.changes[entity] ?? []).find((row) => String(row["id"]) === id);
}

/** Drop a row the server has no copy of, the way `pull` drops a tombstone. */
async function forgetLocalRow(entity: Entity, id: string): Promise<void> {
  if (!idbAvailable()) return;
  await transaction([entity], "readwrite", async (tx) => {
    await tx.store(entity).delete(id);
  });
}

/**
 * Put one row back to what the server holds.
 *
 * Asked for from just under the row's own `updated_seq` rather than from this
 * device's cursor. A refusal changes nothing on the server, so the row has not
 * moved since this device last saw it and an ordinary pull would come back
 * without it — which is exactly why a reload never healed a discard. Nothing
 * else in the answer is used and the cursor is left alone: the rows above it are
 * still owed to the next real pull.
 */
async function restoreFromServer(entity: Entity, id: string): Promise<void> {
  const local = await readLocal(entity, id);
  const seq = typeof local?.["updated_seq"] === "number" ? local["updated_seq"] : 0;
  // A row the server has never acknowledged has no copy up there to ask about:
  // the op being discarded was the only thing that would have created it.
  const row = seq > 0 ? await fetchServerRow(entity, id, seq - 1) : undefined;
  if (!row || row["deleted"] === 1 || row["deleted"] === true) {
    await forgetLocalRow(entity, id);
    return;
  }
  // Landed as sent, deliberately without `mergeIncomingRating`: that merge keeps
  // the value this device holds whenever the server redacts a blind rating, and
  // the value this device holds is the one being thrown away.
  await getOutboxStorage().commit(entity, row, null);
}

/**
 * Throw a refused write away and leave the row saying what everyone else's says.
 *
 * Dropping the dead letter is the easy half and used to be the only half. The
 * rest of it stayed: the device went on showing the value the server had
 * refused, the badge went back to "everything has reached the server", and the
 * two never converged — no device ever writes that row again, so no pull ever
 * carries it. One phone said `Pure luck` and the server said `Skill dominates`,
 * for good, with nothing on screen admitting they disagreed.
 *
 * Restored from the server rather than from a pre-edit value kept back on this
 * device, and the difference is not academic. What the dialog promises is what
 * *everyone* keeps, and only the server knows that: another device may have
 * moved the row since, and while a rating is blind the server will not even tell
 * this device what it holds — so a locally retained number could put back
 * something nobody else has, which is the same silent divergence wearing a nicer
 * value. Taking the server's copy converges by construction, including when the
 * honest answer is "blank until the reveal".
 *
 * Restored *before* the dead letter is dropped: a restore that cannot reach the
 * server must leave the refusal on screen rather than swallow it quietly.
 */
export async function discardDeadLetter(id: string): Promise<void> {
  const record = (await deadLetters()).find((letter) => letter.op_id === id);
  if (record) await restoreFromServer(record.op.entity, record.op.id);
  await dropDeadLetter(id);
  await refreshCounts();
}

// ---------------------------------------------------------------------------
// Backup
// ---------------------------------------------------------------------------

export interface Backup {
  format: "punchboard-backup";
  version: 1;
  exported_at: number;
  cursor: number;
  data: Partial<Record<StoreName, unknown[]>>;
}

/**
 * A round-trippable dump of everything on this device.
 *
 * Installed-PWA storage is evictable under pressure — "persisted" is a hint,
 * not a promise, and Safari has cleared origins that asked nicely. Export is
 * therefore load-bearing, not a nicety: it is the only copy of an unsynced
 * session that survives an eviction.
 */
export async function exportJson(): Promise<Backup> {
  const data: Partial<Record<StoreName, unknown[]>> = {};
  for (const store of [...SYNCABLE, OUTBOX_STORE, CONFLICT_STORE, DEAD_STORE] as StoreName[]) {
    data[store] = await getAll(store);
  }
  const backup: Backup = {
    format: "punchboard-backup",
    version: 1,
    exported_at: Date.now(),
    cursor: await getCursor(),
    data,
  };
  await setMeta(BACKUP_KEY, backup.exported_at);
  return backup;
}

/** Convenience for the settings screen: a file the user can actually save. */
export async function exportBlob(): Promise<Blob> {
  return new Blob([JSON.stringify(await exportJson(), null, 2)], { type: "application/json" });
}

/**
 * Restore a backup. Rows are merged, not wiped over: a restore on a device
 * that has since collected its own unsynced work must not destroy that work.
 * Queued ops are re-appended so they still reach the server.
 */
export async function importJson(input: Backup | string): Promise<number> {
  const backup: Backup = typeof input === "string" ? (JSON.parse(input) as Backup) : input;
  if (backup.format !== "punchboard-backup") throw new Error("Not a Punchboard backup");
  let restored = 0;
  for (const entity of SYNCABLE) {
    const rows = backup.data[entity] as Record<string, unknown>[] | undefined;
    if (!rows?.length) continue;
    await bulkPut(entity, rows);
    restored += rows.length;
  }
  for (const store of [OUTBOX_STORE, CONFLICT_STORE, DEAD_STORE] as StoreName[]) {
    const rows = backup.data[store] as Record<string, unknown>[] | undefined;
    if (!rows?.length) continue;
    if (store === OUTBOX_STORE) {
      // Drop `seq` so the local auto-increment assigns fresh, ordered keys.
      await bulkPut(
        store,
        rows.map(({ seq: _drop, ...rest }) => rest),
      );
    } else {
      await bulkPut(store, rows);
    }
    restored += rows.length;
  }
  // Rewind rather than trust the imported cursor: pulling a little twice is
  // harmless, pulling too little loses rows.
  await setCursor(Math.min(backup.cursor, await getCursor()));
  await refreshCounts();
  return restored;
}

/** Milliseconds since the last export, or null if never exported. */
export async function lastBackupAgo(): Promise<number | null> {
  const at = await getMeta<number>(BACKUP_KEY);
  return at === undefined ? null : Date.now() - at;
}

/** Nuke local state. Only for an explicit "sign out and forget this device". */
export async function wipeLocal(): Promise<void> {
  await transaction(ALL_STORES, "readwrite", async (tx) => {
    for (const store of ALL_STORES) await tx.store(store).clear();
  });
  setState({ pending: 0, conflicts: 0, deadLetters: 0, cursor: 0 });
}

export { conflicts, deadLetters, pendingCount, subscribeOutbox };
export { resolveConflict, retryDeadLetter } from "./outbox";
export type { SidelinedOp, OutboxOp, Resolution } from "./outbox";
export type { Entity };
